import React from 'react';
import { Tooltip } from '@mui/material';
import { usePermissions } from '../contexts/PermissionContext';

interface PermissionWrapperProps {
  moduleName: string;
  operation: 'view' | 'add' | 'edit' | 'delete';
  miniModuleName?: string;
  children: React.ReactElement;
  tooltipTitle?: string;
  disabled?: boolean;
  hideWhenNoAccess?: boolean;
  fallback?: React.ReactNode;
  disabledStyle?: React.CSSProperties;
  [key: string]: any;
}

/**
 * Versatile permission wrapper component
 * - Wraps any component with permission checking
 * - Can hide, disable, or show tooltip when no access
 * - Zero performance overhead (uses cached permissions)
 * - Flexible styling options
 */
const PermissionWrapper: React.FC<PermissionWrapperProps> = ({
  moduleName,
  operation,
  miniModuleName,
  children,
  tooltipTitle,
  disabled = false,
  hideWhenNoAccess = false,
  fallback = null,
  disabledStyle = {},
  ...props
}) => {
  const { hasPermission, loading } = usePermissions();

  // Fast permission check (O(1) from cache)
  const hasAccess = hasPermission(moduleName, operation, miniModuleName);
  const shouldDisable = disabled || !hasAccess;

  // Show children during loading to avoid layout shift
  if (loading) {
    return children;
  }

  // Hide component if no access and hideWhenNoAccess is true
  if (!hasAccess && hideWhenNoAccess) {
    return <>{fallback}</>;
  }

  const defaultDisabledStyle: React.CSSProperties = {
    opacity: 0.5,
    pointerEvents: 'none',
    cursor: 'not-allowed',
    filter: 'grayscale(0.8)',
    ...disabledStyle,
  };

  const getTooltipTitle = (): string => {
    if (tooltipTitle) return tooltipTitle;
    if (!hasAccess && !loading) {
      const target = miniModuleName ? `${moduleName} > ${miniModuleName}` : moduleName;
      return `Vous n'avez pas la permission ${operation} pour ${target}`;
    }
    return '';
  };

  // Clone child and apply disabled style if needed
  const wrappedChild = React.cloneElement(children, {
    ...props,
    disabled: shouldDisable,
    style: {
      ...children.props.style,
      ...(shouldDisable ? defaultDisabledStyle : {}),
    },
  });

  const tooltipContent = getTooltipTitle();
  if (tooltipContent && shouldDisable) {
    return (
      <Tooltip title={tooltipContent} arrow>
        <span style={{ display: 'inline-block' }}>{wrappedChild}</span>
      </Tooltip>
    );
  }

  return wrappedChild;
};

export default PermissionWrapper;
